/**
 * Supplier Return Form Modal
 *
 * Modal to create or edit a supplier return.
 * Two modes, determined by the caller:
 * - Free entry (no receiptId) — user picks supplier manually
 * - Linked to receipt (receiptId provided) — supplier and products are pre-filled
 *   from the validated goods receipt
 *
 * @see US-40.3 - Frontend retours fournisseur
 */

import { useState, useEffect, useMemo, useCallback } from 'react';
import { Undo2, Save, Trash2 } from 'lucide-react';
import {
  Button,
  Modal,
  Select,
  Input,
  Textarea,
  Table,
  TableHeader,
  TableBody,
  TableRow,
  TableHead,
  TableCell,
  TableFooter,
} from '@/components/ui';
import { DatePicker } from '@/components/ui';
import type { DateRange } from '@/components/ui';
import { formatCentimes } from '@/lib/format';
import { useCurrency } from '@/hooks';
import { useQuery } from '@tanstack/react-query';
import {
  useCreateSupplierReturn,
  useUpdateSupplierReturn,
  useSupplierReturn,
} from '../hooks/useSupplierReturns';
import {
  SupplierReturnItemReason,
  SUPPLIER_RETURN_ITEM_REASON_LABELS,
} from '@bstock/shared';
import type { CreateSupplierReturnDto, SupplierCatalogItemWithDetails } from '@bstock/shared';
import { useActiveSuppliers } from '@/features/suppliers/hooks/useSuppliers';
import { useCatalogItems } from '@/features/suppliers/hooks';
import { goodsReceiptsService } from '@/features/goods-receipts/services/goods-receipts.service';
import { ProductSearchAutocomplete } from '@/features/purchase-orders/components/ProductSearchAutocomplete';

interface SupplierReturnFormModalProps {
  returnId?: string | null;
  receiptId?: string | null;
  onClose: () => void;
}

interface PackagingOption {
  catalogItemId: string;
  packagingName: string;
  unitPriceExclTax: number;
  conversionToRecipeUnit: number;
}

interface FormItem {
  id: string; // local temp id
  rawProductId: string;
  rawProductName: string;
  rawProductSku: string | null;
  packagingName: string;
  conversionToRecipeUnit: number;
  quantity: number;
  unitPriceExclTax: number;
  returnReason: SupplierReturnItemReason;
  notes: string;
  packagingOptions: PackagingOption[];
  selectedCatalogItemId: string;
}

let tempIdCounter = 0;
function generateTempId() {
  return `temp-${++tempIdCounter}`;
}

export function SupplierReturnFormModal({
  returnId,
  receiptId,
  onClose,
}: SupplierReturnFormModalProps) {
  const currency = useCurrency();
  const isEdit = !!returnId;
  const isLinked = !!receiptId && !isEdit;

  // Load existing return if editing
  const { data: existingReturn } = useSupplierReturn(returnId ?? null);

  // Suppliers (active only for dropdown)
  const { data: suppliers = [] } = useActiveSuppliers();

  // Load full receipt details when a receiptId is provided (linked mode)
  const { data: linkedReceiptDetail } = useQuery({
    queryKey: ['goods-receipts', 'detail', receiptId],
    queryFn: () => goodsReceiptsService.getById(receiptId!),
    enabled: isLinked,
    staleTime: 5 * 60 * 1000,
  });

  // Mutations
  const createMutation = useCreateSupplierReturn();
  const updateMutation = useUpdateSupplierReturn();

  // Form state
  const [supplierId, setSupplierId] = useState('');

  // Fetch all catalog items for the selected supplier (for packaging options)
  // Same pattern as OrderItemsTable — must be loaded before adding items
  const { data: catalogData } = useCatalogItems(supplierId || null, {
    isActive: true,
    limit: 100,
  });
  const catalogByProduct = useMemo(() => {
    const allCatalogItems = catalogData?.data ?? [];
    const grouped: Record<string, typeof allCatalogItems> = {};
    for (const item of allCatalogItems) {
      if (!grouped[item.rawProductId]) {
        grouped[item.rawProductId] = [];
      }
      grouped[item.rawProductId].push(item);
    }
    return grouped;
  }, [catalogData]);
  const [returnDate, setReturnDate] = useState(
    new Date().toISOString().split('T')[0]
  );
  const [reason, setReason] = useState('');
  const [notes, setNotes] = useState('');
  const [items, setItems] = useState<FormItem[]>([]);

  // Populate form when editing
  useEffect(() => {
    if (existingReturn && isEdit) {
      setSupplierId(existingReturn.supplierId);
      setReturnDate(existingReturn.returnDate.split('T')[0]);
      setReason(existingReturn.reason ?? '');
      setNotes(existingReturn.notes ?? '');
      setItems(
        existingReturn.items.map((item) => ({
          id: generateTempId(),
          rawProductId: item.rawProductId,
          rawProductName: item.rawProductName,
          rawProductSku: item.rawProductSku ?? null,
          packagingName: item.packagingName,
          conversionToRecipeUnit: item.conversionToRecipeUnit,
          quantity: item.quantity,
          unitPriceExclTax: item.unitPriceExclTax,
          returnReason: item.returnReason as SupplierReturnItemReason,
          notes: item.notes ?? '',
          packagingOptions: [],
          selectedCatalogItemId: '',
        }))
      );
    }
  }, [existingReturn, isEdit]);

  // Auto-fill from linked receipt
  useEffect(() => {
    if (!linkedReceiptDetail) return;
    setSupplierId(linkedReceiptDetail.supplier.id);
    setItems(
      linkedReceiptDetail.items.map((item) => ({
        id: generateTempId(),
        rawProductId: item.rawProductId,
        rawProductName: item.rawProductName,
        rawProductSku: item.rawProduct?.sku ?? null,
        packagingName: item.packagingName,
        conversionToRecipeUnit: item.conversionToRecipeUnit,
        quantity: item.quantityReceived,
        unitPriceExclTax: item.unitPriceExclTax,
        returnReason: SupplierReturnItemReason.DAMAGED,
        notes: '',
        packagingOptions: [],
        selectedCatalogItemId: '',
      }))
    );
  }, [linkedReceiptDetail]);

  // Back-fill packaging options for items loaded without them (edit / linked mode)
  // Runs whenever catalog data becomes available
  useEffect(() => {
    if (Object.keys(catalogByProduct).length === 0) return;
    setItems((prev) =>
      prev.map((item) => {
        if (item.packagingOptions.length > 1) return item; // already populated with multiple options
        const variants = catalogByProduct[item.rawProductId] ?? [];
        if (variants.length === 0) return item;
        const packagingOptions: PackagingOption[] = variants.map((ci) => ({
          catalogItemId: ci.id,
          packagingName: `${ci.packaging.name} (${ci.conversionToRecipeUnit} ${ci.rawProduct.recipeUnit.symbol})`,
          unitPriceExclTax: ci.currentPrice?.priceExclTax ?? 0,
          conversionToRecipeUnit: ci.conversionToRecipeUnit,
        }));
        // Try to match current packaging by conversionToRecipeUnit
        const matched = packagingOptions.find(
          (o) => o.conversionToRecipeUnit === item.conversionToRecipeUnit
        );
        return {
          ...item,
          packagingOptions,
          selectedCatalogItemId: matched?.catalogItemId ?? packagingOptions[0].catalogItemId,
        };
      })
    );
  }, [catalogByProduct, items.length]);

  // Supplier change — clear items when supplier actually changes
  const handleSupplierChange = useCallback(
    (newSupplierId: string) => {
      if (newSupplierId !== supplierId && items.length > 0) {
        setItems([]);
      }
      setSupplierId(newSupplierId);
    },
    [supplierId, items.length]
  );

  // Add item from catalog autocomplete — variants passed by ProductSearchAutocomplete
  const addItemFromCatalog = useCallback(
    (catalogItem: SupplierCatalogItemWithDetails, variants: SupplierCatalogItemWithDetails[]) => {
      const packagingOptions: PackagingOption[] = variants.map((ci) => ({
        catalogItemId: ci.id,
        packagingName: `${ci.packaging.name} (${ci.conversionToRecipeUnit} ${ci.rawProduct.recipeUnit.symbol})`,
        unitPriceExclTax: ci.currentPrice?.priceExclTax ?? 0,
        conversionToRecipeUnit: ci.conversionToRecipeUnit,
      }));

      setItems((prev) => [
        ...prev,
        {
          id: generateTempId(),
          rawProductId: catalogItem.rawProductId,
          rawProductName: catalogItem.rawProduct.name,
          rawProductSku: catalogItem.rawProduct.sku ?? null,
          packagingName: `${catalogItem.packaging.name} (${catalogItem.conversionToRecipeUnit} ${catalogItem.rawProduct.recipeUnit.symbol})`,
          conversionToRecipeUnit: catalogItem.conversionToRecipeUnit ?? 1,
          quantity: 1,
          unitPriceExclTax: catalogItem.currentPrice?.priceExclTax ?? 0,
          returnReason: SupplierReturnItemReason.DAMAGED,
          notes: '',
          packagingOptions,
          selectedCatalogItemId: catalogItem.id,
        },
      ]);
    },
    [catalogByProduct]
  );

  // Remove item
  const removeItem = useCallback((id: string) => {
    setItems((prev) => prev.filter((item) => item.id !== id));
  }, []);

  // Handle packaging selection change
  const handlePackagingChange = useCallback((itemId: string, catalogItemId: string) => {
    setItems((prev) =>
      prev.map((item) => {
        if (item.id !== itemId) return item;
        const pkg = item.packagingOptions.find((o) => o.catalogItemId === catalogItemId);
        if (!pkg) return item;
        return {
          ...item,
          selectedCatalogItemId: catalogItemId,
          packagingName: pkg.packagingName,
          unitPriceExclTax: pkg.unitPriceExclTax,
          conversionToRecipeUnit: pkg.conversionToRecipeUnit,
        };
      })
    );
  }, []);

  // Update item field
  const updateItem = useCallback(
    (id: string, field: keyof FormItem, value: string | number | SupplierReturnItemReason) => {
      setItems((prev) =>
        prev.map((item) => (item.id === id ? { ...item, [field]: value } : item))
      );
    },
    []
  );

  // Calculate total
  const totalExclTax = useMemo(
    () => items.reduce((sum, item) => sum + item.quantity * item.unitPriceExclTax, 0),
    [items]
  );

  // Validate form
  const isValid = useMemo(() => {
    if (!supplierId) return false;
    if (!returnDate) return false;
    if (items.length === 0) return false;
    return items.every(
      (item) =>
        item.rawProductName &&
        item.quantity > 0 &&
        item.returnReason
    );
  }, [supplierId, returnDate, items]);

  // Submit
  const handleSubmit = useCallback(async () => {
    const dto: CreateSupplierReturnDto = {
      supplierId,
      returnDate,
      reason: reason || undefined,
      notes: notes || undefined,
      items: items.map((item) => ({
        rawProductId: item.rawProductId,
        rawProductName: item.rawProductName,
        packagingName: item.packagingName,
        conversionToRecipeUnit: item.conversionToRecipeUnit,
        quantity: item.quantity,
        unitPriceExclTax: item.unitPriceExclTax,
        returnReason: item.returnReason,
        notes: item.notes || undefined,
      })),
    };

    if (isEdit && returnId) {
      updateMutation.mutate(
        { id: returnId, data: dto },
        { onSuccess: onClose }
      );
    } else {
      createMutation.mutate(dto, { onSuccess: onClose });
    }
  }, [
    supplierId,
    returnDate,
    reason,
    notes,
    items,
    isEdit,
    returnId,
    createMutation,
    updateMutation,
    onClose,
  ]);

  const isSaving = createMutation.isPending || updateMutation.isPending;

  // Subtitle for the header
  const linkedReceiptNumber =
    linkedReceiptDetail?.receiptNumber ??
    existingReturn?.goodsReceiptNumber ??
    null;

  // DatePicker value
  const returnDateRange: DateRange = {
    start: returnDate ? new Date(returnDate + 'T12:00:00') : null,
    end: returnDate ? new Date(returnDate + 'T12:00:00') : null,
  };

  const handleDateChange = useCallback((range: DateRange) => {
    if (range.start) {
      const d = range.start;
      const y = d.getFullYear();
      const m = String(d.getMonth() + 1).padStart(2, '0');
      const day = String(d.getDate()).padStart(2, '0');
      setReturnDate(`${y}-${m}-${day}`);
    }
  }, []);

  const supplierOptions = useMemo(
    () => suppliers?.map((s) => ({ value: s.id, label: s.name })) ?? [],
    [suppliers]
  );

  return (
    <Modal isOpen onClose={onClose} size="5xl">
      {/* Header */}
      <div className="flex items-center justify-between mb-6">
        <div className="flex items-center gap-3">
          <div className="p-2 bg-primary-100 rounded-lg">
            <Undo2 className="w-5 h-5 text-primary-600" />
          </div>
          <div>
            <h2 className="text-xl font-semibold text-dark-500">
              {isEdit ? 'Modifier le retour' : 'Nouveau retour fournisseur'}
            </h2>
            <p className="text-sm text-neutral-500">
              {linkedReceiptNumber
                ? `Reception ${linkedReceiptNumber}`
                : 'Retour libre'}
            </p>
          </div>
        </div>

        {/* Actions */}
        <div className="flex items-center gap-3">
          <Button variant="ghost" onClick={onClose} disabled={isSaving}>
            Annuler
          </Button>
          <Button
            variant="secondary"
            onClick={handleSubmit}
            disabled={!isValid || isSaving}
          >
            {isSaving ? (
              <span className="flex items-center gap-2">
                <span className="animate-spin">...</span>
                Enregistrement...
              </span>
            ) : (
              <>
                <Save className="w-4 h-4" />
                {isEdit ? 'Enregistrer' : 'Enregistrer (brouillon)'}
              </>
            )}
          </Button>
        </div>
      </div>

      {/* Content - scrollable area */}
      <div className="max-h-[calc(100vh-12rem)] overflow-auto space-y-6">

        {/* Section: Informations */}
        <div className="bg-neutral-50 rounded-lg border border-neutral-200 p-6">
          <h3 className="text-lg font-semibold text-dark-500 mb-4">Informations</h3>
          <div className="grid grid-cols-1 md:grid-cols-3 gap-6">
            <Select
              label="Fournisseur *"
              value={supplierId}
              onChange={handleSupplierChange}
              disabled={isLinked && !!linkedReceiptDetail}
              options={supplierOptions}
              placeholder="Selectionner un fournisseur"
            />

            <div>
              <label className="block text-sm font-medium text-dark-500 mb-1.5">
                Date du retour *
              </label>
              <DatePicker
                asSingle
                value={returnDateRange}
                onChange={handleDateChange}
                placeholder="Selectionner une date"
              />
            </div>

            <Input
              label="Motif global"
              placeholder="Motif du retour (optionnel)"
              value={reason}
              onChange={(e) => setReason(e.target.value)}
            />
          </div>
        </div>

        {/* Section: Produits retournes */}
        <div className="bg-neutral-50 rounded-lg border border-neutral-200 p-6">
          <div className="flex items-center justify-between mb-4">
            <h3 className="text-lg font-semibold text-dark-500">
              Produits retournes
            </h3>
          </div>

          {/* Product search autocomplete — free mode only */}
          {!isLinked && (
            <div className="mb-4">
              {supplierId ? (
                <ProductSearchAutocomplete
                  supplierId={supplierId}
                  onSelect={addItemFromCatalog}
                  placeholder="Rechercher un produit a retourner..."
                />
              ) : (
                <p className="text-sm text-neutral-500 italic">
                  Selectionnez un fournisseur pour ajouter des produits
                </p>
              )}
            </div>
          )}

          {items.length === 0 ? (
            <div className="text-center py-12 text-neutral-500">
              Ajoutez des produits a retourner
            </div>
          ) : (
            <Table>
              <TableHeader>
                <TableRow>
                  <TableHead>Produit</TableHead>
                  <TableHead className="w-[80px]">Cond.</TableHead>
                  <TableHead className="w-[80px] text-right">Qte</TableHead>
                  <TableHead className="w-[120px] text-right">PU HT</TableHead>
                  <TableHead className="w-[120px] text-right">Total HT</TableHead>
                  <TableHead className="w-[160px]">Motif</TableHead>
                  <TableHead className="w-[50px]"></TableHead>
                </TableRow>
              </TableHeader>
              <TableBody>
                {items.map((item) => (
                  <TableRow key={item.id}>
                    <TableCell>
                      <div>
                        <span className="font-medium">{item.rawProductName}</span>
                        {item.rawProductSku && (
                          <span className="block text-xs text-neutral-500">
                            {item.rawProductSku}
                          </span>
                        )}
                      </div>
                    </TableCell>
                    <TableCell>
                      {item.packagingOptions.length > 1 ? (
                        <Select
                          value={item.selectedCatalogItemId}
                          onChange={(value) => handlePackagingChange(item.id, value)}
                          options={item.packagingOptions.map((opt) => ({
                            value: opt.catalogItemId,
                            label: opt.packagingName,
                          }))}
                          className="w-48"
                        />
                      ) : (
                        <span className="text-neutral-600 text-sm">{item.packagingName}</span>
                      )}
                    </TableCell>
                    <TableCell className="text-right">
                      <Input
                        type="number"
                        min={1}
                        max={1000}
                        value={String(item.quantity)}
                        onChange={(e) =>
                          updateItem(item.id, 'quantity', Math.min(1000, Math.max(0, parseInt(e.target.value) || 0)))
                        }
                        className="w-24 text-right"
                      />
                    </TableCell>
                    <TableCell className="text-right text-neutral-600">
                      {formatCentimes(item.unitPriceExclTax, currency)}
                    </TableCell>
                    <TableCell className="text-right font-medium">
                      {formatCentimes(item.quantity * item.unitPriceExclTax, currency)}
                    </TableCell>
                    <TableCell>
                      <Select
                        value={item.returnReason}
                        onChange={(value) =>
                          updateItem(
                            item.id,
                            'returnReason',
                            value as SupplierReturnItemReason
                          )
                        }
                        options={Object.entries(SUPPLIER_RETURN_ITEM_REASON_LABELS).map(
                          ([key, label]) => ({ value: key, label })
                        )}
                      />
                    </TableCell>
                    <TableCell>
                      <Button
                        variant="ghost"
                        size="sm"
                        onClick={() => removeItem(item.id)}
                      >
                        <Trash2 className="w-4 h-4 text-danger-500" />
                      </Button>
                    </TableCell>
                  </TableRow>
                ))}
              </TableBody>
              <TableFooter>
                <TableRow>
                  <TableCell colSpan={4} className="text-right font-semibold">
                    Total HT
                  </TableCell>
                  <TableCell className="text-right font-semibold text-lg">
                    {formatCentimes(totalExclTax, currency)}
                  </TableCell>
                  <TableCell colSpan={2} />
                </TableRow>
              </TableFooter>
            </Table>
          )}
        </div>

        {/* Section: Notes */}
        <div className="bg-neutral-50 rounded-lg border border-neutral-200 p-6">
          <h3 className="text-lg font-semibold text-dark-500 mb-4">Notes</h3>
          <Textarea
            value={notes}
            onChange={(e) => setNotes(e.target.value)}
            placeholder="Notes internes sur ce retour..."
            className="min-h-[80px]"
          />
        </div>

      </div>
    </Modal>
  );
}
